import React, { useState } from "react";
import { Widget, ChartType, AnalyticsTheme } from "./analyticsState";

interface Props {
  widget: Widget | null;
  theme: AnalyticsTheme;
  isDark: boolean;
  isCollapsed?: boolean;
  onToggle: () => void;
  onUpdate: (id: string, updates: Partial<Widget>) => void;
  onUpdateCanvas: (updates: Partial<AnalyticsTheme>) => void;
}

const PropertySidebar: React.FC<Props> = ({
  widget,
  theme,
  isDark,
  isCollapsed,
  onToggle,
  onUpdate,
  onUpdateCanvas,
}) => {
  const [tab, setTab] = useState<"setup" | "style">("style");

  if (!widget) {
    return (
      <div className={`ca-sidebar ${isCollapsed ? "ca-collapsed" : ""}`}>
        <div
          className="ca-sidebar-header"
          style={{
            padding: "16px",
            fontWeight: "600",
            borderBottom: "1px solid var(--ca-border)",
            display: "flex",
            justifyContent: isCollapsed ? "center" : "space-between",
          }}
        >
          {!isCollapsed && <span>Canvas Settings</span>}
          <button
            className="ca-btn-icon"
            onClick={onToggle}
            title="Toggle Canvas Settings"
          >
            <i className={`fa fa-chevron-${isCollapsed ? "left" : "right"}`} />
          </button>
        </div>
        {!isCollapsed && (
          <div className="ca-sidebar-content">
            <div className="ca-sidebar-section">
              <div className="ca-sidebar-section-title">
                Layout & Appearance
              </div>
              <div className="ca-field">
                <label className="ca-field-label">Canvas Width</label>
                <select
                  className="ca-field-select"
                  value={theme.canvasWidth || "100%"}
                  onChange={(e) =>
                    onUpdateCanvas({ canvasWidth: e.target.value })
                  }
                >
                  <option value="100%">100% (Responsive)</option>
                  <option value="1200px">1200px (Fixed)</option>
                  <option value="1440px">1440px (Fixed Wide)</option>
                </select>
              </div>
              <div className="ca-field">
                <label className="ca-field-label">Background Color</label>
                <div className="ca-color-row">
                  <div
                    className="ca-color-swatch"
                    style={{
                      background: theme.canvasBackgroundColor || "#F1F3F4",
                    }}
                  >
                    <input
                      type="color"
                      value={theme.canvasBackgroundColor || "#F1F3F4"}
                      onChange={(e) =>
                        onUpdateCanvas({
                          canvasBackgroundColor: e.target.value,
                        })
                      }
                    />
                  </div>
                  <span
                    className="ca-field-label"
                    style={{ margin: 0, fontSize: "12px" }}
                  >
                    Canvas BG
                  </span>
                </div>
              </div>
              <div className="ca-toggle-row">
                <span className="ca-toggle-label">Show Grid Pattern</span>
                <label className="ca-toggle">
                  <input
                    type="checkbox"
                    checked={theme.showCanvasGridPattern || false}
                    onChange={(e) =>
                      onUpdateCanvas({
                        showCanvasGridPattern: e.target.checked,
                      })
                    }
                  />
                  <span className="ca-toggle-slider" />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const { uiConfig, colors, type } = widget;
  const { axes, specifics, global: globalConfig } = uiConfig;

  const updateSpecifics = (key: string, value: any) => {
    onUpdate(widget.id, {
      uiConfig: {
        ...uiConfig,
        specifics: { ...specifics, [key]: value },
      },
    });
  };

  const updateAxes = (key: string, value: any) => {
    onUpdate(widget.id, {
      uiConfig: {
        ...uiConfig,
        axes: { ...axes, [key]: value },
      },
    });
  };

  const updateGlobal = (key: string, value: any) => {
    onUpdate(widget.id, {
      uiConfig: {
        ...uiConfig,
        global: { ...globalConfig, [key]: value },
      },
    });
  };

  const updateColor = (index: number, color: string) => {
    const newColors = [...colors];
    newColors[index] = color;
    onUpdate(widget.id, { colors: newColors });
  };

  const updateTitle = (title: string) => {
    onUpdate(widget.id, { title });
  };

  const isChartType = !["scorecard", "table", "text", "image"].includes(type);
  const hasAxes = ["bar", "line", "area", "scatter", "heatmap"].includes(type);

  return (
    <div className={`ca-sidebar ${isCollapsed ? "ca-collapsed" : ""}`}>
      <div
        className="ca-sidebar-header"
        style={{
          padding: "16px",
          fontWeight: "600",
          borderBottom: "1px solid var(--ca-border)",
          display: "flex",
          justifyContent: isCollapsed ? "center" : "space-between",
        }}
      >
        {!isCollapsed && <span>Widget Settings</span>}
        <button
          className="ca-btn-icon"
          onClick={onToggle}
          title="Toggle Widget Settings"
        >
          <i className={`fa fa-chevron-${isCollapsed ? "left" : "right"}`} />
        </button>
      </div>

      {!isCollapsed && (
        <>
          <div className="ca-sidebar-tabs">
            <button
              className={`ca-sidebar-tab${tab === "setup" ? " active" : ""}`}
              onClick={() => setTab("setup")}
            >
              Setup
            </button>
            <button
              className={`ca-sidebar-tab${tab === "style" ? " active" : ""}`}
              onClick={() => setTab("style")}
            >
              Style
            </button>
          </div>

          <div className="ca-sidebar-content">
            {tab === "setup" && (
              <>
                <div className="ca-sidebar-section">
                  <div className="ca-sidebar-section-title">Widget Info</div>
                  <div className="ca-field">
                    <label className="ca-field-label">Title</label>
                    <input
                      className="ca-field-input"
                      value={widget.title}
                      onChange={(e) => updateTitle(e.target.value)}
                    />
                  </div>
                  <div className="ca-field">
                    <label className="ca-field-label">Type</label>
                    <div className="ca-theme-badge">
                      <i className={`fa ${getIconForType(type)}`} />
                      {getLabelForType(type)}
                    </div>
                  </div>
                </div>

                {type === "scorecard" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">
                      Scorecard Data
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Value</label>
                      <input
                        className="ca-field-input"
                        value={specifics.scorecardValue || ""}
                        onChange={(e) =>
                          updateSpecifics("scorecardValue", e.target.value)
                        }
                      />
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Label</label>
                      <input
                        className="ca-field-input"
                        value={specifics.scorecardLabel || ""}
                        onChange={(e) =>
                          updateSpecifics("scorecardLabel", e.target.value)
                        }
                      />
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Prefix</label>
                      <input
                        className="ca-field-input"
                        value={specifics.scorecardPrefix || ""}
                        onChange={(e) =>
                          updateSpecifics("scorecardPrefix", e.target.value)
                        }
                        placeholder="e.g. $"
                      />
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Suffix</label>
                      <input
                        className="ca-field-input"
                        value={specifics.scorecardSuffix || ""}
                        onChange={(e) =>
                          updateSpecifics("scorecardSuffix", e.target.value)
                        }
                        placeholder="e.g. %"
                      />
                    </div>
                  </div>
                )}

                {type === "text" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Text Content</div>
                    <div className="ca-field">
                      <label className="ca-field-label">Content</label>
                      <textarea
                        className="ca-field-input"
                        rows={5}
                        value={specifics.textContent || ""}
                        onChange={(e) =>
                          updateSpecifics("textContent", e.target.value)
                        }
                        style={{ resize: "vertical" }}
                      />
                    </div>
                  </div>
                )}

                {type === "image" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Image Source</div>
                    <div className="ca-field">
                      <label className="ca-field-label">Image URL</label>
                      <input
                        className="ca-field-input"
                        value={specifics.imageUrl || ""}
                        onChange={(e) =>
                          updateSpecifics("imageUrl", e.target.value)
                        }
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {tab === "style" && (
              <>
                <div className="ca-sidebar-section">
                  <div className="ca-sidebar-section-title">Widget Border</div>
                  <div className="ca-toggle-row">
                    <span className="ca-toggle-label">Show Border</span>
                    <label className="ca-toggle">
                      <input
                        type="checkbox"
                        checked={globalConfig.showBorder || false}
                        onChange={(e) =>
                          updateGlobal("showBorder", e.target.checked)
                        }
                      />
                      <span className="ca-toggle-slider" />
                    </label>
                  </div>
                  {globalConfig.showBorder && (
                    <>
                      <div className="ca-field" style={{ marginTop: 8 }}>
                        <label className="ca-field-label">Border Color</label>
                        <div className="ca-color-row">
                          <div
                            className="ca-color-swatch"
                            style={{
                              background: globalConfig.borderColor || "#DADCE0",
                            }}
                          >
                            <input
                              type="color"
                              value={globalConfig.borderColor || "#DADCE0"}
                              onChange={(e) =>
                                updateGlobal("borderColor", e.target.value)
                              }
                            />
                          </div>
                          <span
                            className="ca-field-label"
                            style={{ margin: 0, fontSize: "12px" }}
                          >
                            Color
                          </span>
                        </div>
                      </div>
                      <div className="ca-field">
                        <label className="ca-field-label">Border Width</label>
                        <div className="ca-slider-row">
                          <input
                            type="range"
                            className="ca-slider"
                            min={1}
                            max={10}
                            value={globalConfig.borderWidth || 1}
                            onChange={(e) =>
                              updateGlobal(
                                "borderWidth",
                                Number(e.target.value),
                              )
                            }
                          />
                          <span className="ca-slider-value">
                            {globalConfig.borderWidth || 1}px
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="ca-sidebar-section">
                  <div className="ca-sidebar-section-title">
                    Widget Settings
                  </div>
                  <div className="ca-field" style={{ marginTop: 8 }}>
                    <label className="ca-field-label">Background Color</label>
                    <div className="ca-color-row">
                      <div
                        className="ca-color-swatch"
                        style={{
                          background:
                            globalConfig.backgroundColor ||
                            (isDark ? "#1E1E1E" : "#FFFFFF"),
                        }}
                      >
                        <input
                          type="color"
                          value={
                            globalConfig.backgroundColor ||
                            (isDark ? "#1E1E1E" : "#FFFFFF")
                          }
                          onChange={(e) =>
                            updateGlobal("backgroundColor", e.target.value)
                          }
                        />
                      </div>
                      <span
                        className="ca-field-label"
                        style={{ margin: 0, fontSize: "12px" }}
                      >
                        Color
                      </span>
                    </div>
                  </div>
                  <div className="ca-toggle-row">
                    <span className="ca-toggle-label">All Sides Resizable</span>
                    <label className="ca-toggle">
                      <input
                        type="checkbox"
                        checked={globalConfig.resizeAllSides !== false}
                        onChange={(e) =>
                          updateGlobal("resizeAllSides", e.target.checked)
                        }
                      />
                      <span className="ca-toggle-slider" />
                    </label>
                  </div>
                </div>

                {isChartType && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Colors</div>
                    <div className="ca-colors-grid">
                      {colors.slice(0, 6).map((c, i) => (
                        <div
                          key={i}
                          className="ca-color-swatch"
                          style={{ background: c }}
                        >
                          <input
                            type="color"
                            value={c}
                            onChange={(e) => updateColor(i, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {type === "scorecard" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Value Color</div>
                    <div className="ca-color-row">
                      <div
                        className="ca-color-swatch"
                        style={{ background: colors[0] }}
                      >
                        <input
                          type="color"
                          value={colors[0]}
                          onChange={(e) => updateColor(0, e.target.value)}
                        />
                      </div>
                      <span className="ca-field-label" style={{ margin: 0 }}>
                        Value color
                      </span>
                    </div>
                  </div>
                )}

                {isChartType && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Legend</div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Show Legend</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={specifics.showLegend !== false}
                          onChange={(e) =>
                            updateSpecifics("showLegend", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                    {specifics.showLegend !== false && (
                      <div className="ca-field" style={{ marginTop: 8 }}>
                        <label className="ca-field-label">Position</label>
                        <select
                          className="ca-field-select"
                          value={specifics.legendPosition || "bottom"}
                          onChange={(e) =>
                            updateSpecifics("legendPosition", e.target.value)
                          }
                        >
                          <option value="top">Top</option>
                          <option value="bottom">Bottom</option>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    )}
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Data Labels</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={specifics.showDataLabels || false}
                          onChange={(e) =>
                            updateSpecifics("showDataLabels", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                  </div>
                )}

                {hasAxes && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Axes</div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Show Gridlines</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={axes.showGridlines}
                          onChange={(e) =>
                            updateAxes("showGridlines", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">X-Axis</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={axes.showXAxis}
                          onChange={(e) =>
                            updateAxes("showXAxis", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Y-Axis</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={axes.showYAxis}
                          onChange={(e) =>
                            updateAxes("showYAxis", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                  </div>
                )}

                {type === "bar" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">Bar Options</div>
                    <div className="ca-field">
                      <label className="ca-field-label">Orientation</label>
                      <select
                        className="ca-field-select"
                        value={specifics.orientation || "vertical"}
                        onChange={(e) =>
                          updateSpecifics("orientation", e.target.value)
                        }
                      >
                        <option value="vertical">Vertical</option>
                        <option value="horizontal">Horizontal</option>
                      </select>
                    </div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Stacked</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={specifics.stacked || false}
                          onChange={(e) =>
                            updateSpecifics("stacked", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Border Radius</label>
                      <div className="ca-slider-row">
                        <input
                          type="range"
                          className="ca-slider"
                          min={0}
                          max={20}
                          value={specifics.barBorderRadius || 4}
                          onChange={(e) =>
                            updateSpecifics(
                              "barBorderRadius",
                              Number(e.target.value),
                            )
                          }
                        />
                        <span className="ca-slider-value">
                          {specifics.barBorderRadius || 4}px
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {(type === "line" || type === "area") && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">
                      {type === "line" ? "Line" : "Area"} Options
                    </div>
                    <div className="ca-toggle-row">
                      <span className="ca-toggle-label">Smooth Curve</span>
                      <label className="ca-toggle">
                        <input
                          type="checkbox"
                          checked={specifics.smooth || false}
                          onChange={(e) =>
                            updateSpecifics("smooth", e.target.checked)
                          }
                        />
                        <span className="ca-toggle-slider" />
                      </label>
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Stroke Width</label>
                      <div className="ca-slider-row">
                        <input
                          type="range"
                          className="ca-slider"
                          min={1}
                          max={8}
                          value={specifics.strokeWidth || 3}
                          onChange={(e) =>
                            updateSpecifics(
                              "strokeWidth",
                              Number(e.target.value),
                            )
                          }
                        />
                        <span className="ca-slider-value">
                          {specifics.strokeWidth || 3}px
                        </span>
                      </div>
                    </div>
                    {type === "area" && (
                      <div className="ca-field">
                        <label className="ca-field-label">Fill Opacity</label>
                        <div className="ca-slider-row">
                          <input
                            type="range"
                            className="ca-slider"
                            min={5}
                            max={100}
                            value={Math.round(
                              (specifics.fillOpacity || 0.35) * 100,
                            )}
                            onChange={(e) =>
                              updateSpecifics(
                                "fillOpacity",
                                Number(e.target.value) / 100,
                              )
                            }
                          />
                          <span className="ca-slider-value">
                            {Math.round((specifics.fillOpacity || 0.35) * 100)}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {type === "donut" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">
                      Donut Options
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Inner Radius</label>
                      <div className="ca-slider-row">
                        <input
                          type="range"
                          className="ca-slider"
                          min={20}
                          max={90}
                          value={specifics.innerRadius || 55}
                          onChange={(e) =>
                            updateSpecifics(
                              "innerRadius",
                              Number(e.target.value),
                            )
                          }
                        />
                        <span className="ca-slider-value">
                          {specifics.innerRadius || 55}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {type === "radar" && (
                  <div className="ca-sidebar-section">
                    <div className="ca-sidebar-section-title">
                      Radar Options
                    </div>
                    <div className="ca-field">
                      <label className="ca-field-label">Fill Opacity</label>
                      <div className="ca-slider-row">
                        <input
                          type="range"
                          className="ca-slider"
                          min={5}
                          max={100}
                          value={Math.round(
                            (specifics.fillOpacity || 0.3) * 100,
                          )}
                          onChange={(e) =>
                            updateSpecifics(
                              "fillOpacity",
                              Number(e.target.value) / 100,
                            )
                          }
                        />
                        <span className="ca-slider-value">
                          {Math.round((specifics.fillOpacity || 0.3) * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function getIconForType(type: ChartType): string {
  const map: Record<string, string> = {
    bar: "fa-chart-bar",
    line: "fa-chart-line",
    area: "fa-chart-area",
    pie: "fa-chart-pie",
    donut: "fa-circle-notch",
    radar: "fa-bullseye",
    scatter: "fa-braille",
    heatmap: "fa-th",
    treemap: "fa-th-large",
    funnel: "fa-filter",
    radialBar: "fa-tachometer-alt",
    scorecard: "fa-digital-tachograph",
    table: "fa-table",
    text: "fa-font",
    image: "fa-image",
  };
  return map[type] || "fa-chart-bar";
}

function getLabelForType(type: ChartType): string {
  const map: Record<string, string> = {
    bar: "Bar Chart",
    line: "Line Chart",
    area: "Area Chart",
    pie: "Pie Chart",
    donut: "Donut Chart",
    radar: "Radar Chart",
    scatter: "Scatter Plot",
    heatmap: "Heatmap",
    treemap: "Treemap",
    funnel: "Funnel Chart",
    radialBar: "Gauge / Radial",
    scorecard: "Scorecard",
    table: "Table",
    text: "Text Block",
    image: "Image",
  };
  return map[type] || type;
}

export default PropertySidebar;
